import React, { useState, useEffect } from "react";
import { Modal, Button, Radio, Space, Tag, Spin } from "antd";
import { formatAmount } from "../../config/helpers.js";
import { collection, doc, getDoc, getDocs, increment, query, serverTimestamp, updateDoc } from "firebase/firestore";
import { db } from "../../config/firebase.js";

export default function UnprocessedTicketsModal({ open, onClose, tickets, onRateAapplied }) {
  const [selectedRates, setSelectedRates] = useState({});
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSelectRate = (ticketId, type, rate) => {
    setSelectedRates((prev) => ({
      ...prev,
      [ticketId]: { ...prev[ticketId], [type]: rate },
    }));
  };

  const handleApplyRate = async (ticket) => {
    if (!ticket?.client?.uid) {
      Modal.error({
        title: "Errueur de traitment",
        content: "Impossible de traiter ce ticket car il n'y a aucune entreprise associée",
        okText: "Ok, j'ai compris !"
      });
      return;
    }

    if (!ticket?.driver?.id) {
      Modal.error({
        title: "Errueur de traitment",
        content: "Impossible de traiter ce ticket car il n'y a aucun chauffeur associé",
        okText: "Ok, j'ai compris !"
      });
      return;
    }

    const rates = selectedRates[ticket.uid];

    if (!rates?.companyRate || !rates?.agentRate) {
      alert("Veuillez sélectionner à la fois le tarif entreprise et le tarif agent !");
      return;
    }

    setIsLoading(true);

    try {
      const rateApplied = {
        companyRate: {
          key: rates.companyRate.key,
          label: rates.companyRate.label,
          amount: rates.companyRate.amount,
        },
        agentRate: {
          key: rates.agentRate.key,
          label: rates.agentRate.label,
          amount: rates.agentRate.amount,
        },
        appliedAt: serverTimestamp(),
        appliedBy: "admin"
      };

      await updateDoc(doc(db, "tickets", ticket.uid), {
        rateApplied: rateApplied
      });

      ticket.rateApplied = rateApplied;
      onRateAapplied(ticket)

    } catch (err) {
      console.error("Erreur lors de l'application des tarifs :", err);
      alert("Erreur lors de l'application des tarifs :", err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={900}
      title="Tickets non traités"
      bodyStyle={{ maxHeight: "70vh", overflowY: "auto", padding: 24 }}
    >
      {loading ? (
        <Spin tip="Chargement des tickets..." />
      ) : tickets.length === 0 ? (
        <p>Tous les tickets ont été traités 🎉</p>
      ) : (
        <div className="ticket-list">
          {tickets.map((ticket) => (
            <div key={ticket.uid} className="ticket-card">
              <div className="ticket-header">
                <div>
                  <strong>Ticket #{ticket.ticketNo}</strong> —{" "}
                  {ticket.client?.name || "Client inconnu"}
                </div>
                <Tag color="blue">{ticket.companyRates?.length || 0} tarifs entreprise</Tag>
                <Tag color="green">{ticket.agentRates?.length || 0} tarifs agent</Tag>
              </div>

              <div>
                <div><strong>Service</strong> — {ticket.service || "Service inconnu"}</div>
                <div><strong>Depot</strong> — {ticket.deposer.location.address}</div>
              </div>

              <div className="ticket-body">
                <div className="rate-section">
                  <span className="rate-title">Entreprise</span>
                  <Radio.Group
                    value={selectedRates[ticket.uid]?.companyRate}
                    onChange={(e) => handleSelectRate(ticket.uid, "companyRate", e.target.value)}
                    style={{ display: "flex", flexDirection: "column", gap: 4 }}
                  >
                    {ticket.companyRates.map((rate) => (
                      <Radio key={rate.key} value={rate}>
                        <Space>
                          <span>{rate.label}</span>
                          <strong>{formatAmount(rate.amount)}</strong>
                        </Space>
                      </Radio>
                    ))}
                  </Radio.Group>
                </div>

                {/* Agent Rates */}
                <div className="rate-section">
                  <span className="rate-title">Agent</span>
                  <Radio.Group
                    value={selectedRates[ticket.uid]?.agentRate}
                    onChange={(e) => handleSelectRate(ticket.uid, "agentRate", e.target.value)}
                    style={{ display: "flex", flexDirection: "column", gap: 4 }}
                  >
                    {ticket.agentRates.map((rate) => (
                      <Radio key={rate.key} value={rate}>
                        <Space>
                          <span>{rate.label}</span>
                          <strong>{formatAmount(rate.amount)}</strong>
                        </Space>
                      </Radio>
                    ))}
                  </Radio.Group>
                </div>
              </div>
              <Button
                type="primary"
                style={{
                  marginTop: 12,
                  backgroundColor: isLoading ? "#d9d9d9" : undefined,
                  color: isLoading ? "#888888" : undefined
                }}
                onClick={() => handleApplyRate(ticket)}
                disabled={isLoading}
              >
                Appliquer les tarifs
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Styles */}
      <style>{`
        .ticket-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .ticket-card {
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          background: #fff;
          padding: 16px;
          transition: box-shadow 0.2s ease;
        }

        .ticket-card:hover {
          box-shadow: 0 8px 24px rgba(0,0,0,0.08);
        }

        .ticket-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 12px;
        }

        .ticket-body {
          margin-top: 10px;
          display: flex;
          gap: 32px;
        }

        .rate-section {
          flex: 1;
          background: #f9fafb;
          border-radius: 8px;
          padding: 12px;
        }

        .rate-title {
          font-size: 12px;
          font-weight: 600;
          color: #6b7280;
          text-transform: uppercase;
          display: block;
          margin-bottom: 8px;
        }
      `}</style>
    </Modal>
  );
}
